import { getActiveElevationSource } from '@fit-editor/fit-core'
import type {
  Activity,
  EditFieldOperation,
  EditHistoryState,
  FieldDefsByMesgNum,
} from '@fit-editor/fit-core'

import { EXPORT_MIME_TYPES, type ExportFormat } from '@/lib/download'
import {
  exportActivityToFitAsync,
  exportToCsvAsync,
  exportToGeoJsonAsync,
  exportToGpxAsync,
  exportToTcxAsync,
} from '@/worker/fit-client'

export type { ExportFormat }

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  fit: 'FIT (re-encoded)',
  gpx: 'GPX',
  tcx: 'TCX',
  csv: 'CSV',
  geojson: 'GeoJSON',
}

export const EXPORT_FORMAT_EXTENSIONS: Record<ExportFormat, string> = {
  fit: 'fit',
  gpx: 'gpx',
  tcx: 'tcx',
  csv: 'csv',
  geojson: 'geojson',
}

export interface ExportMessage {
  message: string
}

/**
 * The one shape every format's export result is normalized to, so the
 * UI (`ExportDialog`/`BatchDialog`) only has to handle a single "did it
 * work, what should I disclose, what do I download" contract rather than
 * five slightly different ones. `blocked`/`validationErrors` are only
 * ever populated for `fit` (`export-validation` spec: "Export is blocked
 * on self-validation failure") -- every other format has no equivalent
 * self-validation step, so they're always `false`/`[]`.
 */
export interface ExportOutcome {
  format: ExportFormat
  fileName: string
  mimeType: string
  /** `null` exactly when `blocked` is true -- there is nothing safe to download. */
  data: Uint8Array | string | null
  warnings: ExportMessage[]
  validationErrors: ExportMessage[]
  blocked: boolean
}

function stripKnownExtension(fileName: string): string {
  return fileName.replace(/\.(fit|gpx|tcx|csv|geojson|zip)$/i, '')
}

/**
 * Normalizes a *rejected* `exportActivity` call (a genuine Worker-RPC
 * failure, e.g. the worker thread crashed or `currentActivityAsync` itself
 * rejected) to the same `ExportOutcome` shape a format-level failure
 * already uses -- `blocked: true` plus a `validationErrors` entry. This
 * lets every caller (`ExportDialog`, `BatchDialog`) reuse the single
 * "blocked, here's why, nothing to download" rendering branch they already
 * have for FIT self-validation failures, rather than inventing a second,
 * parallel error-display path for this case.
 */
export function exportFailureOutcome(
  format: ExportFormat,
  baseFileName: string,
  error: unknown,
): ExportOutcome {
  const name = stripKnownExtension(baseFileName)
  return {
    format,
    fileName: `${name}.${EXPORT_FORMAT_EXTENSIONS[format]}`,
    mimeType: EXPORT_MIME_TYPES[format],
    data: null,
    warnings: [],
    validationErrors: [{ message: error instanceof Error ? error.message : String(error) }],
    blocked: true,
  }
}

/**
 * Runs the export for one activity in one format, off the main thread,
 * and normalizes the result to `ExportOutcome`. Never throws for a
 * format-level failure (FIT self-validation failures and encoder
 * exceptions are both already folded into `blocked`/`validationErrors`
 * by `exportActivityToFitAsync` -- see `fit-worker.ts`'s doc comment) --
 * only a genuine worker-communication failure (e.g. the worker itself
 * crashed) would propagate as a rejected promise, which callers should
 * still handle explicitly rather than assume can't happen.
 */
export async function exportActivity(
  activity: Activity,
  format: ExportFormat,
  baseFileName: string,
  fieldDefsByMesgNum: FieldDefsByMesgNum,
  history?: EditHistoryState,
): Promise<ExportOutcome> {
  const outcome = await exportActivityFormat(activity, format, baseFileName, fieldDefsByMesgNum)
  const terrainWarning = history != null ? terrainElevationWarning(history) : null
  if (terrainWarning == null || outcome.blocked) return outcome
  return { ...outcome, warnings: [...outcome.warnings, terrainWarning] }
}

/**
 * Disclosure appended to every export of an activity whose current state
 * includes an applied `setElevation` edit: its elevation values are no
 * longer (all) the device's own measurements, and the data source's
 * license requires attribution.
 */
export function terrainElevationWarning(history: EditHistoryState): ExportMessage | null {
  const source = getActiveElevationSource(history)
  if (source == null) return null
  return {
    message: `Elevation in this export was filled or replaced from ${source.name} terrain data (about ${source.resolutionM} m resolution) rather than measured by the device; ascent/descent are estimates. Data: ${source.attribution}.`,
  }
}

/** How fit-core's FIT self-validation names each summary message kind in its errors ("lap #2 ..."). */
const VALIDATED_MESSAGE_KINDS: Record<string, string> = {
  lapMesgs: 'lap',
  sessionMesgs: 'session',
  activityMesgs: 'activity',
}

/**
 * Which of the user's hand edits (`editField`, see `getActiveFieldEdits`)
 * a FIT self-validation failure is about -- e.g. an edited lap
 * `totalDistance` that no longer matches the records -- as labels such as
 * "lap 2 totalDistance", so the export dialog can say *why* the export is
 * blocked and how to fix it. Matches on the message instance ("lap #2")
 * and the field name in the error text; a `startTime` edit also matches
 * the "covers ... but no record messages" error it can cause.
 */
export function fieldEditsBehindValidationErrors(
  errors: readonly ExportMessage[],
  edits: readonly EditFieldOperation[],
): string[] {
  const labels: string[] = []
  for (const edit of edits) {
    const kind = VALIDATED_MESSAGE_KINDS[edit.messagesKey]
    if (kind == null) continue
    const prefix = `${kind} #${edit.index + 1} `
    const related = errors.some(
      ({ message }) =>
        message.startsWith(prefix) &&
        (message.includes(edit.field) ||
          (edit.field === 'startTime' && message.includes(' covers '))),
    )
    const label = `${kind} ${edit.index + 1} ${edit.field}`
    if (related && !labels.includes(label)) labels.push(label)
  }
  return labels
}

async function exportActivityFormat(
  activity: Activity,
  format: ExportFormat,
  baseFileName: string,
  fieldDefsByMesgNum: FieldDefsByMesgNum,
): Promise<ExportOutcome> {
  const name = stripKnownExtension(baseFileName)
  const fileName = `${name}.${EXPORT_FORMAT_EXTENSIONS[format]}`
  const mimeType = EXPORT_MIME_TYPES[format]

  switch (format) {
    case 'fit': {
      const result = await exportActivityToFitAsync(activity, fieldDefsByMesgNum)
      return {
        format,
        fileName,
        mimeType,
        data: result.blocked ? null : (result.bytes ?? null),
        warnings: result.warnings,
        validationErrors: result.validationErrors,
        blocked: result.blocked,
      }
    }
    case 'gpx': {
      const result = await exportToGpxAsync(activity)
      return {
        format,
        fileName,
        mimeType,
        data: result.xml,
        warnings: result.warnings,
        validationErrors: [],
        blocked: false,
      }
    }
    case 'tcx': {
      const result = await exportToTcxAsync(activity)
      return {
        format,
        fileName,
        mimeType,
        data: result.xml,
        warnings: result.warnings,
        validationErrors: [],
        blocked: false,
      }
    }
    case 'csv': {
      const result = await exportToCsvAsync(activity)
      return {
        format,
        fileName,
        mimeType,
        data: result.csv,
        warnings: result.warnings,
        validationErrors: [],
        blocked: false,
      }
    }
    case 'geojson': {
      const result = await exportToGeoJsonAsync(activity)
      return {
        format,
        fileName,
        mimeType,
        data: JSON.stringify(result.geojson, null, 2),
        warnings: result.warnings,
        validationErrors: [],
        blocked: false,
      }
    }
    default: {
      // Exhaustiveness guard -- if `ExportFormat` ever grows a new
      // member, this is a compile error, not a silent runtime no-op.
      const unreachable: never = format
      throw new Error(`Unsupported export format: ${String(unreachable)}`)
    }
  }
}
